import React from 'react';
import { FileText, Upload, CheckCircle, Clock, Sparkles } from 'lucide-react';

interface AccountValidationWelcomeModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  userName?: string;
}

/**
 * Modal displayed to new users explaining the account validation process.
 * Shows a friendly welcome message with steps to complete their profile
 * and submit documents for validation.
 */
export const AccountValidationWelcomeModal: React.FC<AccountValidationWelcomeModalProps> = ({
  isOpen,
  onConfirm,
  userName,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Decorative gradient header */}
        <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 px-6 py-8 text-white relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full" />
          <div className="absolute -right-4 top-12 w-16 h-16 bg-white/10 rounded-full" />

          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-primary-200" />
              <span className="text-primary-200 text-sm font-medium">Bienvenue sur NEXEDU</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {userName ? `Bonjour ${userName} !` : 'Bienvenue !'}
            </h2>
            <p className="text-primary-100 text-sm">
              Votre compte a été créé avec succès. Une dernière étape avant d'accéder à la plateforme.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Pour valider votre compte, vous devez :
            </h3>

            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Compléter votre profil</p>
                  <p className="text-sm text-gray-500">
                    Renseignez vos informations personnelles et professionnelles
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Upload className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Soumettre les documents requis</p>
                  <p className="text-sm text-gray-500">
                    Téléchargez les pièces justificatives demandées (CIN, CV, diplômes...)
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Soumettre pour validation</p>
                  <p className="text-sm text-gray-500">
                    Une fois soumis, vous aurez accès temporaire pendant l'examen de votre dossier
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Bon à savoir</p>
                <p className="text-sm text-blue-700 mt-1">
                  Après soumission de votre dossier, vous pourrez utiliser la plateforme pendant que
                  l'administration examine votre profil. Vous serez notifié dès la validation.
                </p>
              </div>
            </div>
          </div>

          {/* Action button */}
          <button
            onClick={onConfirm}
            className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25"
          >
            <CheckCircle className="w-5 h-5" />
            Ok, j'ai compris !
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountValidationWelcomeModal;
